import { useMemo } from 'react';
import { Dictionary, keyBy } from 'lodash';
import { fieldMappingSelector } from 'slices/field.slice';
import { useAppSelector } from 'slices/store';
import type {
  TImportErrorKind,
  TObjectImportJob,
  TObjectJob,
} from 'types/objectJob.type';
import { TField } from '@timeedit/types';

export type ErrorTableData = {
  extId?: string;
  column?: string;
  cell?: string;
  message: string;
  errorCode?: number;
  kind?: TImportErrorKind;
};

const FINISHED_STATUSES = ['FINISHED', 'FAILED'];

export const countFailedObjects = (rows: ErrorTableData[]) =>
  new Set(rows.filter((r) => r.extId != null).map((r) => r.extId)).size;

const useErrorTableData = (job: TObjectJob) => {
  const fieldsMap = useAppSelector(fieldMappingSelector);

  return useMemo<ErrorTableData[]>(() => {
    if (job.operationType === 'IMPORT') return getImportErrors(job, fieldsMap);

    if (!FINISHED_STATUSES.includes(job.status)) return [];

    const objectIdMap = keyBy(job.objectsToProcess, 'id');

    return job.batchErrors.flatMap((err) =>
      flattenInternalIdObjects(
        { ...err, kind: 'EXECUTION' as const },
        objectIdMap
      ).flatMap((e) => flattenColumn(e, fieldsMap))
    );
  }, [job, fieldsMap]);
};

export default useErrorTableData;

const flattenInternalIdObjects = <T extends { objects?: number[] }>(
  { objects, ...row }: T,
  idMap: Dictionary<{ id: number; extId: string }>
) => {
  if (objects == null) return [row];
  return objects.map((id) => ({
    ...row,
    extId: idMap[id]?.extId ?? id.toString(),
  }));
};

const flattenImportObjects = <
  T extends { objects?: ({ extId?: string; cell?: string } | string)[] },
>({
  objects,
  ...row
}: T) => {
  if (objects == null || objects.length === 0) return [row];
  return objects.map((obj) => {
    if (typeof obj === 'string') return { ...row, extId: obj };
    return {
      ...row,
      extId: obj.extId,
      cell: obj.cell,
    };
  });
};

const flattenColumn = <T extends { fields?: number[]; column?: string }>(
  { fields, column, ...row }: T,
  fieldsMap: Record<number, TField>
) => {
  if (fields == null || fields.length === 0) return [{ ...row, column }];
  return fields.map((id) => ({
    ...row,
    column: fieldsMap[id]?.extId ?? id.toString(),
  }));
};

const getImportErrors = (
  job: TObjectImportJob,
  fieldsMap: Record<number, TField>
): ErrorTableData[] => {
  return job.batchErrors.flatMap((e) => {
    if (
      (e.kind ?? 'EXECUTION') === 'EXECUTION' &&
      !FINISHED_STATUSES.includes(job.status)
    )
      return [];

    return flattenImportObjects(e).flatMap((e2) =>
      flattenColumn(e2, fieldsMap)
    );
  });
};
